import {Dispatch, FC, SetStateAction, useMemo, useState} from "react";
import {FlowCondition, FlowTrigger} from "../../../../../lib/src/api/repositories/flows-repository";
import {
  availableConditionFieldsByTriggerType,
  BooleanField, DateField,
  NumericField,
  OptionsField,
  StringField
} from "./availableConditionFieldsByTriggerType";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash} from "@fortawesome/free-solid-svg-icons";
import {Button} from "../../../../../lib/src/components/form/Button";
import {Select} from "../../../../../lib/src/components/form/Select";
import { Input } from "@jane/lib/src/components/form/Input";

export const ConditionsEditor: FC<{trigger: FlowTrigger, conditions: FlowCondition[], setConditions: Dispatch<SetStateAction<FlowCondition[]>>}> = ({trigger, conditions, setConditions}) => {
  const availableFields = useMemo(() => {
    return availableConditionFieldsByTriggerType[trigger.type] ?? []
  }, [trigger.type])
  const [addField, setAddField] = useState<string>('0')
  return <div className={'flex flex-col items-stretch px-3'}>
    {conditions.map((condition, index) => {
      const field = availableFields.find(f => f.field === condition.field)
      if (! field) {
        return <div key={index} className={'text-brand-error rounded-lg bg-brand-primary-container px-4 py-3'}>
          <p>Deze voorwaarde is niet meer beschikbaar. Verwijder deze voorwaarde om verder te gaan.</p>
          <button type={'button'} className={'text-brand-error underline'} onClick={() => {
            setConditions(conditions.filter((_, i) => i !== index))
          }}>Verwijder</button>
        </div>
      }
      return <div key={index}>
        {index !== 0 && <>
          <div className={'ml-[calc(0.75rem-1px)] border-l-2 border-brand-primary-container h-2'}></div>
          <div className={'flex'}>
            <div className={'px-1 border border-brand-primary-container rounded-sm text-sm uppercase text-brand-primary'}>EN</div>
          </div>
          <div className={'ml-[calc(0.75rem-1px)] border-l-2 border-brand-primary-container h-2'}></div>
        </>}
        <div className={'rounded-lg bg-brand-primary-container text-brand-on-primary-container px-4 py-3'}>
          <div className={'flex justify-between'}>
            <h2 className={'font-medium'}>{field.label}</h2>
            <button className={'text-brand-error'} type={'button'} onClick={() => setConditions(conditions.filter((_, i) => i !== index))}><FontAwesomeIcon icon={faTrash} /></button>
          </div>
          {field.type === 'string' && <StringFieldConditionEditor field={field} condition={condition} setCondition={(c) => {
            setConditions(conditions.map((v, i) => i === index ? c : v))
          }} />}
          {field.type === 'numeric' && <NumericFieldConditionEditor field={field} condition={condition} setCondition={(c) => {
            setConditions(conditions.map((v, i) => i === index ? c : v))
          }} />}
          {field.type === 'boolean' && <BooleanFieldConditionEditor field={field} condition={condition} setCondition={(c) => {
            setConditions(conditions.map((v, i) => i === index ? c : v))
          }} />}
          {field.type === 'options' && <OptionsFieldConditionEditor field={field} condition={condition} setCondition={(c) => {
            setConditions(conditions.map((v, i) => i === index ? c : v))
          }} />}
          {field.type === 'date' && <DateFieldConditionEditor field={field} condition={condition} setCondition={(c) => {
            setConditions(conditions.map((v, i) => i === index ? c : v))
          }} />}
        </div>
      </div>
    })}
    <div className={'ml-[calc(0.75rem-1px)] border-l-2 border-brand-primary-container h-4'}></div>
    <div className={'text-brand-on-primary-container rounded-lg border-2 border-dashed bg-white border-brand-primary-container px-4 py-3'}>
      <h2 className={'font-medium'}>Voorwaarde toevoegen</h2>
      <div className={'flex flex-col gap-2 mt-2'}>
        <Select label={''} options={Object.fromEntries(Object.entries(availableFields).map(e => [e[0], e[1].label]))} value={addField} onChange={setAddField} />
        <Button type={'secondary'} size={'sm'} text={'Toevoegen'} onClick={() => {
          const field = availableFields[parseInt(addField)]
          if (! field) {
            return
          }
          setConditions([...conditions, {field: field.field, operator: 'eq', value: field.defaultValue}])
          setAddField('0')
        }} />
      </div>
    </div>
  </div>
}

const StringFieldConditionEditor: FC<{field: StringField, condition: FlowCondition, setCondition: (v: FlowCondition) => void}> = ({field, condition, setCondition}) => {
  return <div>
    <Input type={'text'} label={'is'} value={condition.value.toString()} onChange={(v) => {
      setCondition({...condition, operator: 'eq', value: v})
    }} />
  </div>
}

const NumericFieldConditionEditor: FC<{field: NumericField, condition: FlowCondition, setCondition: (v: FlowCondition) => void}> = ({field, condition, setCondition}) => {
  const operators = {
    'eq': 'is gelijk aan',
    'gt': 'is groter dan',
    'lt': 'is kleiner dan',
    'gte': 'is groter of gelijk aan',
    'lte': 'is kleiner of gelijk aan',
  }
  return <div className={'flex items-center gap-2'}>
    <Select label={''} options={operators} value={condition.operator} onChange={(v) => {
      setCondition({...condition, operator: v as "eq" | "gt" | "gte" | "lt" | "lte"})
    }} />
    <Input type={'number'} label={''} value={condition.value as number} onChange={(v) => {
      setCondition({...condition, value: v})
    }} />
  </div>
}

const BooleanFieldConditionEditor: FC<{field: BooleanField, condition: FlowCondition, setCondition: (v: FlowCondition) => void}> = ({field, condition, setCondition}) => {
  const values = {
    'true': 'is waar',
    'false': 'is niet waar',
  }
  return <div>
    <Select label={''} options={values} value={condition.value ? 'true' : 'false'} onChange={(v) => {
      setCondition({...condition, operator: 'eq', value: v === 'true'})
    }} />
  </div>
}

const OptionsFieldConditionEditor: FC<{field: OptionsField, condition: FlowCondition, setCondition: (v: FlowCondition) => void}> = ({field, condition, setCondition}) => {
  const keys = Object.fromEntries(field.options.map(o => [o, o]))
  return <div>
    <Select label={'is'} options={keys} value={condition.value as string} onChange={(v) => {
      setCondition({...condition, operator: 'eq', value: v})
    }} />
  </div>
}

const DateFieldConditionEditor: FC<{field: DateField, condition: FlowCondition, setCondition: (v: FlowCondition) => void}> = ({field, condition, setCondition}) => {
  const keys = {
    'today': 'Vandaag',
    'future': 'In de toekomst',
    'past': 'In het verleden',
  }
  return <div>
    <Select label={'is'} options={keys} value={condition.value as string} onChange={(v) => {
      setCondition({...condition, operator: 'dc', value: v})
    }} />
  </div>
}